import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Users, Loader2 } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import axios from 'axios';

export const Conferences = () => {
  const [conferences, setConferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadConferences();
  }, []);

  const loadConferences = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.get(`${BACKEND_URL}/api/conferences`);
      console.log('Conferences loaded:', response.data);
      setConferences(response.data || []);
      setError(null);
    } catch (err) {
      console.error('Error loading conferences:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-xl">Loading conferences...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-red-600">Error: {error}</p>
          <Button onClick={loadConferences} className="mt-4">Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-br from-blue-700 via-blue-800 to-indigo-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Calendar className="h-16 w-16 mx-auto mb-6 text-blue-200" />
          <h1 className="text-5xl md:text-6xl font-bold mb-4" style={{fontFamily: 'Work Sans'}}>Professional Conferences</h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">Join industry leaders, policymakers, and professionals in transformative discussions</p>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {conferences.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-xl text-gray-600">No conferences available at the moment.</p>
          </div>
        ) : (
          <>
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-2">Upcoming Conferences</h2>
              <p className="text-gray-600">Register now to secure your spot</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {conferences.map(conf => (
                <Card key={conf.id} className="hover:shadow-2xl transition-all duration-300 border-t-4 border-t-blue-600">
                  <CardHeader>
                    <CardTitle className="text-xl mb-2">{conf.title}</CardTitle>
                    <CardDescription className="line-clamp-3 text-base">{conf.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3 mb-6">
                      <div className="flex items-center text-sm text-gray-700">
                        <Calendar className="h-4 w-4 mr-2 text-blue-600" />
                        <span className="font-medium">{conf.date}</span>
                      </div>
                      <div className="flex items-center text-sm text-gray-700">
                        <MapPin className="h-4 w-4 mr-2 text-blue-600" />
                        <span className="font-medium">{conf.mode}</span>
                      </div>
                      {conf.target_audience && conf.target_audience.length > 0 && (
                        <div className="flex items-start text-sm text-gray-700">
                          <Users className="h-4 w-4 mr-2 text-blue-600 mt-0.5" />
                          <span className="font-medium">{conf.target_audience.slice(0, 2).join(', ')}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex justify-between items-center pt-4 border-t">
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Investment</p>
                        <span className="text-2xl font-bold text-blue-700">₦{conf.fee.toLocaleString()}</span>
                      </div>
                      <Link to={`/conferences/${conf.id}`}>
                        <Button className="px-6">Register Now</Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};